(() => {
  if (window.hevisynxUnifiedCatalog) return;
  const root = document.querySelector('[data-hxu-catalog]');
  if (!root) return;
  window.hevisynxUnifiedCatalog = true;
  root.classList.add('hxu-enhanced');
  const input = root.querySelector('[data-hxu-search] input[name="q"]');
  const feedback = root.querySelector('[data-hxu-feedback]');
  const message = root.querySelector('[data-hxu-feedback-text]');
  const retry = root.querySelector('[data-hxu-retry]');
  const tagMap = JSON.parse(root.querySelector('[data-hxu-tag-map]').textContent);
  const cache = new Map(), parser = new DOMParser();
  let sequence = 0, controller, timer, composing = false, desired = new URL(location.href);
  root.querySelector('[data-hxu-sidebar-panel]').open = matchMedia('(min-width: 901px)').matches;

  function cleanURL(target) {
    const url = new URL(target, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid Catalog URL');
    ['preview_theme_id', '_fd', 'pb'].forEach(key => {
      const value = new URL(location.href).searchParams.get(key);
      if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
    });
    ['section_id', 'view', 'type', 'options[prefix]'].forEach(key => url.searchParams.delete(key));
    return url;
  }
  function collectionURL(url) {
    const target = cleanURL(url);
    target.searchParams.delete('q');
    if (url.searchParams.get('q')?.trim()) target.searchParams.delete('page');
    if (target.searchParams.get('sort_by') === 'relevance') target.searchParams.delete('sort_by');
    target.hash = '';
    return target;
  }
  if (!desired.searchParams.get('q') || !desired.searchParams.has('page')) {
    cache.set(collectionURL(desired).href, root.querySelector('[data-hxu-content]').outerHTML);
  }
  function syncQuery(query) {
    if (input.value !== query) input.value = query;
    document.dispatchEvent(new CustomEvent('hevisynx:catalog-query-sync', {detail: {query}}));
  }
  function loading() {
    root.setAttribute('aria-busy', 'true');
    root.classList.remove('has-search-error');
    feedback.hidden = false;
    message.textContent = root.dataset.loadingLabel;
    retry.hidden = true;
  }
  async function fetchText(url, signal) {
    const response = await fetch(url, {signal, credentials: 'same-origin', headers: {'X-Requested-With': 'XMLHttpRequest'}});
    if (!response.ok) throw new Error('Catalog request failed');
    return response.text();
  }
  async function getCollection(url, signal) {
    const target = collectionURL(url);
    if (cache.has(target.href)) return cache.get(target.href);
    const endpoint = new URL(target);
    endpoint.searchParams.set('section_id', root.dataset.sectionId);
    const doc = parser.parseFromString(await fetchText(endpoint, signal), 'text/html');
    const content = doc.querySelector('[data-hxu-content]');
    if (!content) throw new Error('Missing Catalog content');
    if (cache.size >= 12) cache.delete(cache.keys().next().value);
    cache.set(target.href, content.outerHTML);
    return content.outerHTML;
  }
  function searchURL(url, query, referenceMatch = false) {
    const endpoint = new URL(root.dataset.searchUrl, location.origin);
    const suffix = decodeURIComponent(url.pathname).split('/collections/all/')[1] || '';
    const tags = suffix.split('+').map(tag => tagMap[tag]).filter(Boolean);
    const escapeTerm = value => value.replace(/[\\():"]/g, '\\$&');
    const terms = tags.length ? ['(' + (referenceMatch ? query : escapeTerm(query)) + ')', ...tags.map(tag => 'tag:"' + tag.replace(/[\\"]/g, '\\$&') + '"')].join(' AND ') : query;
    endpoint.searchParams.set('q', terms);
    endpoint.searchParams.set('type', 'product');
    endpoint.searchParams.set('options[prefix]', 'last');
    endpoint.searchParams.set('options[unavailable_products]', 'last');
    endpoint.searchParams.set('view', 'hevisynx-catalog');
    const sort = url.searchParams.get('sort_by');
    if (sort && !['manual', 'best-selling'].includes(sort)) endpoint.searchParams.set('sort_by', sort);
    ['page', 'preview_theme_id', '_fd', 'pb'].forEach(key => {
      if (url.searchParams.has(key)) endpoint.searchParams.set(key, url.searchParams.get(key));
    });
    return endpoint;
  }
  async function getSearch(url, query, signal) {
    const resolved = await window.hevisynxReferenceSearch?.resolve(query);
    if (signal.aborted) throw new DOMException('Search cancelled', 'AbortError');
    return fetchText(searchURL(url, resolved?.query || query, Boolean(resolved?.products?.length)), signal);
  }
  function preserveLinks(content, url, query) {
    content.querySelectorAll('[data-hxu-link], .hxu-pagination a[href]').forEach(link => {
      const target = cleanURL(link.href);
      if (link.closest('.hxu-pagination')) {
        const page = target.searchParams.get('page');
        target.pathname = url.pathname;
        target.search = url.search;
        if (page) target.searchParams.set('page', page);
        else target.searchParams.delete('page');
      } else {
        target.searchParams.delete('page');
        const sort = url.searchParams.get('sort_by');
        if (sort) target.searchParams.set('sort_by', sort);
      }
      if (query) target.searchParams.set('q', query);
      else target.searchParams.delete('q');
      link.href = target.href;
    });
  }
  async function update(target, options = {}) {
    const url = cleanURL(target), query = url.searchParams.get('q') || '';
    desired = url;
    const request = ++sequence;
    controller?.abort();
    controller = new AbortController();
    const signal = controller.signal;
    const timeout = setTimeout(() => { if (request === sequence) controller.abort(); }, 15000);
    loading();
    try {
      const [collectionHTML, searchHTML] = await Promise.all([
        getCollection(url, signal),
        query.trim() ? getSearch(url, query.trim(), signal) : Promise.resolve(null)
      ]);
      if (request !== sequence) return;
      const next = parser.parseFromString(collectionHTML, 'text/html').querySelector('[data-hxu-content]');
      if (searchHTML !== null) {
        const response = parser.parseFromString(searchHTML, 'text/html').querySelector('[data-hxu-search-response]');
        if (!response?.querySelector('[data-hxu-search-results]')) throw new Error('Missing search results');
        next.querySelector('#CatalogResults').innerHTML = response.querySelector('[data-hxu-search-results]').innerHTML;
        next.querySelector('[data-hxu-count]').textContent = response.querySelector('[data-hxu-search-count]').textContent;
        const select = next.querySelector('[data-hxu-sort]');
        select.innerHTML = response.querySelector('[data-hxu-search-sort]').innerHTML;
        if (url.searchParams.has('sort_by') && select.value !== url.searchParams.get('sort_by')) url.searchParams.set('sort_by', select.value);
      }
      preserveLinks(next, url, query);
      const openGroups = new Set(Array.from(root.querySelectorAll('[data-hxu-category][open]'), el => el.dataset.hxuCategory));
      const focusKey = document.activeElement?.dataset.focusKey;
      const sidebarScroll = root.querySelector('.hxu-sidebar-body')?.scrollTop || 0;
      const sidebarOpen = root.querySelector('[data-hxu-sidebar-panel]').open;
      root.querySelector('[data-hxu-content]').replaceWith(next);
      root.querySelector('[data-hxu-sidebar-panel]').open = sidebarOpen;
      root.querySelectorAll('[data-hxu-category]').forEach(group => { if (openGroups.has(group.dataset.hxuCategory)) group.open = true; });
      root.querySelector('.hxu-sidebar-body').scrollTop = sidebarScroll;
      if (options.history !== false && url.href !== location.href) history[options.replace ? 'replaceState' : 'pushState']({hxuCatalog: true}, '', url);
      desired = url;
      syncQuery(query);
      feedback.hidden = true;
      root.querySelector('[data-hxu-status]').textContent = root.querySelector('[data-hxu-count]').textContent;
      if (options.pagination) {
        const results = root.querySelector('#CatalogResults');
        results.focus({preventScroll: true});
        results.scrollIntoView({block: 'start', behavior: 'instant'});
      } else if (focusKey) {
        Array.from(root.querySelectorAll('[data-focus-key]')).find(el => el.dataset.focusKey === focusKey)?.focus({preventScroll: true});
      }
    } catch (error) {
      if (request !== sequence) return;
      root.classList.add('has-search-error');
      message.textContent = document.documentElement.lang.startsWith('zh') ? '加载失败，请重试。' : 'Could not load results. Please try again.';
      retry.hidden = false;
      feedback.hidden = false;
    } finally {
      clearTimeout(timeout);
      if (request === sequence) root.removeAttribute('aria-busy');
    }
  }
  function queueQuery(query, immediate = false) {
    clearTimeout(timer);
    sequence++;
    controller?.abort();
    const url = cleanURL(desired);
    if (query.trim()) url.searchParams.set('q', query);
    else url.searchParams.delete('q');
    url.searchParams.delete('page');
    desired = url;
    syncQuery(query);
    loading();
    if (immediate) update(url);
    else timer = setTimeout(() => update(url, {replace: true}), 250);
  }
  input.addEventListener('compositionstart', () => { composing = true; });
  document.addEventListener('compositionstart', event => {
    if (event.target !== input && !event.target.matches('[data-hevisynx-header-search] input[name="q"]')) return;
    clearTimeout(timer);
    sequence++;
    controller?.abort();
  });
  input.addEventListener('compositionend', () => { composing = false; queueQuery(input.value); });
  input.addEventListener('input', () => { if (!composing) queueQuery(input.value); });
  root.querySelector('[data-hxu-search]').addEventListener('submit', event => {
    event.preventDefault();
    queueQuery(input.value, true);
  });
  document.addEventListener('hevisynx:catalog-query', event => {
    if (typeof event.detail?.query === 'string') queueQuery(event.detail.query);
  });
  root.addEventListener('click', event => {
    const link = event.target.closest('[data-hxu-link], .hxu-pagination a[href], [data-hxu-clear-search]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    clearTimeout(timer);
    if (link.hasAttribute('data-hxu-clear-search')) return queueQuery('', true);
    const url = cleanURL(link.href);
    if (input.value.trim()) url.searchParams.set('q', input.value);
    else url.searchParams.delete('q');
    update(url, {pagination: Boolean(link.closest('.hxu-pagination'))});
  });
  root.addEventListener('change', event => {
    if (!event.target.matches('[data-hxu-sort]')) return;
    clearTimeout(timer);
    const url = cleanURL(desired);
    url.searchParams.set('sort_by', event.target.value);
    url.searchParams.delete('page');
    update(url);
  });
  retry.addEventListener('click', () => update(desired));
  window.addEventListener('popstate', () => {
    clearTimeout(timer);
    syncQuery(new URL(location.href).searchParams.get('q') || '');
    update(location.href, {history: false});
  });
  syncQuery(desired.searchParams.get('q') || '');
  if (input.value.trim()) update(desired, {history: false});
})();
